<template>
    <div>
        <div v-if="currentUserIsOrganizationAdmin && currentUserOrganizationUnitId == organizationUnitId" id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 v-if="organizationUnitId" :tid="organizationUnitId"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">
                        {{ isServiceUser ? "Manage service account's roles" : "Manage user's roles" }}
                    </h1>

                    <button @click="$router.go(-1)" class="btn btn-link btn-sm">
                        <i class="fa fa-arrow-left" aria-hidden="true"></i>
                        <span>Back</span>
                    </button>
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div v-if="id && user" class="container">
            <h4 class="fg-color-blue">
                {{displayName}} is currently {{ roles.join(', ') }}
                <span v-if="isServiceUser" class="badge badge-info ml-2">Service Account</span>
            </h4>
            
            <!-- Informational alert for service accounts -->
            <div v-if="isServiceUser" class="alert alert-info mt-3" role="alert">
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                <strong>Service Account Roles:</strong> These roles determine the API access permissions for this service account. The roles control what actions the service account can perform through the API.
            </div>

            <br />
            <div class="row">
                <div v-if="currentUserIsSystemAdmin || currentUserIsOrganizationAdmin" class="col-sm-6 mb-4">
                    <div class="card text-white" :class="[isBusinessUser ? 'bg-primary' : 'bg-secondary']">
                        <div class="card-header">Business User</div>
                        <div class="card-body">
                            <p class="card-text" style="height: 170px;">
                                <span v-if="isServiceUser">Service accounts in this role have API access to:</span>
                                <span v-else>Users in this role have an access to individual dashboards and shared assets. They permissions include:</span>
                                <ul>
                                    <li v-if="!isServiceUser">Sign in to transparentChoice</li>
                                    <li>Access shared assets</li>
                                    <li v-if="!isServiceUser">Use dashboard</li>
                                    <li v-if="isServiceUser">Read-only API operations</li>
                                </ul>
                            </p>
                        </div>
                        <div class="card-footer text-center">
                            <button v-if="!isBusinessUser" @click="handleAssignBusinessUserRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Assign Business User</button>
                            <button v-else @click="handleRevokeBusinessUserRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Revoke Business User</button>
                        </div>
                    </div>
                </div>

                <div v-if="currentUserIsSystemAdmin || currentUserIsOrganizationAdmin" class="col-sm-6 mb-4">
                    <div class="card text-white" :class="[isWorkspaceUser ? 'bg-primary' : 'bg-secondary']">
                        <div class="card-header">Workspace User</div>
                        <div class="card-body">
                            <p class="card-text" style="height: 170px;">
                                <span v-if="isServiceUser">Service accounts in this role have API access to:</span>
                                <span v-else>Users in this role have an access to workspaces and its portfolios. They permissions include:</span>
                                <ul>
                                    <li>Creating a new portfolios</li>
                                    <li>Managing projects</li>
                                    <li>Managing criteria</li>
                                    <li v-if="!isServiceUser">Managing surveys</li>
                                    <li v-if="!isServiceUser">Browsing results</li>
                                    <li v-if="isServiceUser">Full workspace API operations</li>
                                </ul>
                            </p>
                        </div>
                        <div class="card-footer text-center">
                            <button v-if="!isWorkspaceUser" @click="handleAssignWorkspaceUserRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Assign Workspace User</button>
                            <button v-else @click="handleRevokeWorkspaceUserRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Revoke Workspace User</button>
                        </div>
                    </div>
                </div>

                <div v-if="currentUserIsSystemAdmin || currentUserIsGlobalAdmin || currentUserIsOrganizationAdmin" class="col-sm-6 mb-4">
                    <div class="card text-white" :class="[isOrganizationAdmin ? 'bg-primary' : 'bg-secondary']">
                        <div class="card-header">Organization Admin</div>
                        <div class="card-body">
                            <p class="card-text" style="height: 170px;">
                                <span v-if="isServiceUser">Service accounts in this role have API access to:</span>
                                <span v-else>Users in this role have an access to admin panel and can manage within a single organizations. They permissions include:</span>
                                <ul>
                                    <li>Managing users</li>
                                    <li>Managing workspaces</li>
                                    <li>Importing portfolios</li>
                                    <li v-if="isServiceUser">Organization-level API operations</li>
                                </ul>
                            </p>
                        </div>
                        <div class="card-footer text-center">
                            <button v-if="!isOrganizationAdmin" @click="handleAssignOrganizationAdminRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Assign Organization Admin</button>
                            <button v-else @click="handleRevokeOrganizationAdminRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Revoke Organization Admin</button>
                        </div>
                    </div>
                </div>

                <div v-if="currentUserIsSystemAdmin || currentUserIsGlobalAdmin" class="col-sm-6 mb-4">
                    <div class="card text-white" :class="[isGlobalAdmin ? 'bg-primary' : 'bg-secondary']">
                        <div class="card-header">Global Admin</div>
                        <div class="card-body">
                            <p class="card-text" style="height: 170px;">
                                <span v-if="isServiceUser">Service accounts in this role have API access to:</span>
                                <span v-else>Users in this role have an access to admin panel and can set up organizations. They permissions include:</span>
                                <ul>
                                    <li>Setting up a new organizations</li>
                                    <li>Assign global admins</li>
                                    <li>Assign organization admins</li>
                                    <li v-if="isServiceUser">System-wide API operations</li>
                                </ul>
                            </p>
                        </div>
                        <div class="card-footer text-center">
                            <button v-if="!isGlobalAdmin" @click="handleAssignGlobalAdminRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Assign Global Admin</button>
                            <button v-else @click="handleRevokeGlobalAdminRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Revoke Global Admin</button>
                        </div>
                    </div>
                </div>

            </div>
        </div>

        <div v-else-if="!id" class="container">
            <div class="input-group mb-3">
                <input v-model="emailinput" type="email" class="form-control" placeholder="Find user by email">
                <div class="input-group-append">
                    <button @click="handleFindUser" class="btn btn-outline-secondary" type="button">Find</button>
                </div>
            </div>
        </div>

        <div v-else class="container">
            <h4>Email '{{id}}' not found</h4>
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, mixins } from 'vue-facing-decorator';
    import PageBase from "@common/PageBase";
    import Queries from "@Organizations/Users/queries-vue-component";
    import Commands from "@Organizations/Users/commands-vue-component";
    import "@common/AdminNavigationV2.vue";

    @Component
    export default class ManageUserRolesPage extends mixins(PageBase, Queries, Commands) {
        mounted() {
            this.setTitle("Manage user's roles");
            this.runCurrentUserQueryV1(false, false, true);
            this.runUserQueryV1(this.id);
        }

        get userSessionDetails() { return this.model?.UserSessionDetails; }
        get userWithSensitiveData() { return this.model?.UserWithSensitiveData; }

        get currentUserOrganizationUnitId() { return this.userSessionDetails?.OrganizationUnitId; }
        get currentUserIsSystemAdmin() { return this.userSessionDetails?.IsSystemAdmin || false; }
        get currentUserIsGlobalAdmin() { return this.userSessionDetails?.IsGlobalAdmin || false; }
        get currentUserIsOrganizationAdmin() { return this.userSessionDetails?.IsOrganizationAdmin || false; }

        get user() { return this.userWithSensitiveData; }
        get email() { return this.user?.Email; }
        get displayName() { return this.user?.DisplayName; }
        get isSystemAdmin() { return this.user?.IsSystemAdmin; }
        get isGlobalAdmin() { return this.user?.IsGlobalAdmin; }
        get isOrganizationAdmin() { return this.user?.IsOrganizationAdmin; }
        get isWorkspaceUser() { return this.user?.IsWorkspaceUser; }
        get isBusinessUser() { return this.user?.IsBusinessUser; }
        get isServiceUser() { return this.user?.IsServiceUser || false; }
        get organizationUnitId() { return this.user?.OrganizationUnitId; }

        get roles() {
            const roles = [];
            if (this.isSystemAdmin) roles.push('System Admin');
            if (this.isGlobalAdmin) roles.push('Global Admin');
            if (this.isOrganizationAdmin) roles.push('Organization Admin');
            if (this.isWorkspaceUser) roles.push('Workspace User');
            if (this.isWorkspaceUser) roles.push('Business User');
            if (this.isServiceUser) roles.push('Service Account');
            return roles;
        }

        succeedAnyCommand() {
            this.runUserQueryV1(this.id);
        }

        failedAnyCommand(status) {
            if (status.Auth === false) this.navigateTo('/#/signin');
        }

        handleAssignGlobalAdminRoleCommandV1() {
            const accountType = this.isServiceUser ? 'service account' : 'user';
            if (confirm(`Do you really want to assign Global Admin role for ${accountType} ${this.email}?`))
                this.invokeAssignGlobalAdminRoleCommandV1(this.id);
        }

        handleRevokeGlobalAdminRoleCommandV1() {
            const accountType = this.isServiceUser ? 'service account' : 'user';
            if (confirm(`Do you really want to revoke Global Admin role for ${accountType} ${this.email}?`))
                this.invokeRevokeGlobalAdminRoleCommandV1(this.id);
        }

        handleAssignOrganizationAdminRoleCommandV1() {
            const accountType = this.isServiceUser ? 'service account' : 'user';
            if (confirm(`Do you really want to assign Organization Admin role for ${accountType} ${this.email}?`))
                this.invokeAssignOrganizationAdminRoleCommandV1(this.id);
        }

        handleRevokeOrganizationAdminRoleCommandV1() {
            const accountType = this.isServiceUser ? 'service account' : 'user';
            if (confirm(`Do you really want to revoke Organization Admin role for ${accountType} ${this.email}?`))
                this.invokeRevokeOrganizationAdminRoleCommandV1(this.id);
        }

        handleAssignWorkspaceUserRoleCommandV1() {
            const accountType = this.isServiceUser ? 'service account' : 'user';
            if (confirm(`Do you really want to assign Workspace User role for ${accountType} ${this.email}?`))
                this.invokeAssignWorkspaceUserRoleCommandV1(this.id);
        }

        handleRevokeWorkspaceUserRoleCommandV1() {
            const accountType = this.isServiceUser ? 'service account' : 'user';
            if (confirm(`Do you really want to revoke Workspace User role for ${accountType} ${this.email}?`))
                this.invokeRevokeWorkspaceUserRoleCommandV1(this.id);
        }

        handleAssignBusinessUserRoleCommandV1() {
            const accountType = this.isServiceUser ? 'service account' : 'user';
            if (confirm(`Do you really want to assign Business User role for ${accountType} ${this.email}?`))
                this.invokeAssignBusinessUserRoleCommandV1(this.id);
        }

        handleRevokeBusinessUserRoleCommandV1() {
            const accountType = this.isServiceUser ? 'service account' : 'user';
            if (confirm(`Do you really want to revoke Business User role for ${accountType} ${this.email}?`))
                this.invokeRevokeBusinessUserRoleCommandV1(this.id);
        }

        handleFindUser() {
            this.navigateTo(`/#/admin/organizations/users/manage/roles/${this.emailinput}`);
        }
    }
</script>

<style scoped>
</style>